import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { MemoryBoxFormSkeleton } from './MemoryBoxFormSkeleton';
import { Box } from 'models/Box';
import { useModal } from 'xstate/hooks/useModal';
import { useSaveBox, Variables } from 'networking/mindfeed/hooks/useSaveBox';
import { AccessLevel } from 'models/AccessLevel';
import { closeModal, openBoxModal } from 'xstate/actors/ModalActor';
import { requestToSelectBox } from 'xstate/actors/SpaceActor';
import { faLock, faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import RadioField from 'elements/RadioField/RadioField';
import { useApiValidationErrors } from 'networking/mindfeed/hooks/useApiValidationErrors';
import TextInput from 'elements/TextInput';
import { BoxModalStep } from 'xstate/machines/ModalMachine';
import Button from 'elements/Button';
import { useTranslations } from 'next-intl';

interface Props {
  box?: Box;
  isLoading?: boolean;
}

interface FormData {
  accessLevel: AccessLevel;
  name: string;
}

export const MemoryBoxForm: React.FC<Props> = ({ box, isLoading = false }) => {
  const t = useTranslations();
  const { isOpen } = useModal();
  const isEditing = Boolean(box);

  const {
    control,
    register,
    handleSubmit,
    setFocus,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onSubmit',
    defaultValues: {
      accessLevel: AccessLevel.Private,
      name: '',
    },
  });

  const { handleApiError } = useApiValidationErrors<FormData>({ setError });

  const saveBoxMutation = useSaveBox({
    onSuccess: (savedBox) => {
      requestToSelectBox(savedBox);
      reset();
    },
  });

  const handleComplete = (targetBox: Box, accessLevel: AccessLevel) => {
    if (accessLevel === AccessLevel.Shared) {
      openBoxModal(targetBox, {
        step: BoxModalStep.ManagePermissions,
        canNavigateBack: true,
      });
    } else {
      closeModal();
    }
  };

  const onSubmit = async (data: FormData) => {
    if (isEditing) {
      const hasChanges =
        data.accessLevel !== box!.accessLevel || data.name !== box!.name;

      if (!hasChanges) {
        handleComplete(box!, data.accessLevel);
        return;
      }
    }

    clearErrors();

    try {
      const savedBox = await saveBoxMutation.mutateAsync({
        box: {
          ...(isEditing && { id: box!.id }),
          ...data,
        } as Variables['box'],
      });

      handleComplete(savedBox, data.accessLevel);
    } catch (error) {
      handleApiError(error);
    }
  };

  useEffect(() => {
    if (!box) return;

    reset({
      accessLevel: box.accessLevel || AccessLevel.Private,
      name: box.name || '',
    });
  }, [box, reset]);

  useEffect(() => {
    if (!isOpen) return;
    setFocus('name');
  }, [isOpen, setFocus]);

  if (isLoading || saveBoxMutation.isPending) {
    return <MemoryBoxFormSkeleton />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.container}>
      <div className={styles.fields}>
        <div className={styles.nameInput}>
          <TextInput
            placeholder={t('boxes.form.namePlaceholder')}
            label={t('common.name')}
            errors={errors}
            autoComplete="off"
            data-1p-ignore
            {...register('name', {
              required: t('boxes.form.nameRequired'),
              minLength: {
                value: 2,
                message: t('forms.tooShort', { min: 2 }),
              },
            })}
          />
          <p className={styles.helper}>{t('boxes.form.helper')}</p>
        </div>

        <Controller
          name="accessLevel"
          control={control}
          render={({ field }) => (
            <RadioField
              label={t('boxSettings.accessEditorTitle')}
              value={field.value}
              options={[
                {
                  value: AccessLevel.Private,
                  title: t('boxSettings.private'),
                  subtitle: t('boxSettings.privateSubtitle'),
                  icon: faLock,
                },
                {
                  value: AccessLevel.Shared,
                  title: t('boxSettings.shared'),
                  subtitle: t('boxSettings.sharedSubtitle'),
                  icon: faUserGroup,
                },
              ]}
              onChange={(v) => field.onChange(v as AccessLevel)}
            />
          )}
        />
      </div>

      <div className={styles.footer}>
        <Button
          type="button"
          size="sm"
          variant="solid"
          color="neutral"
          onClick={closeModal}
        >
          {t('common.cancel')}
        </Button>
        <Button size="sm" type="submit">
          {isEditing ? t('common.save') : t('common.create')}
        </Button>
      </div>
    </form>
  );
};

const styles = {
  container: 'px-6',
  fields: 'pb-6',
  nameInput: 'mb-5',
  helper: 'mt-2 text-sm text-gray-500',
  footer: 'flex shrink-0 justify-end gap-4',
};

export default MemoryBoxForm;
